import { useRTL } from '@/shared/hooks';
import { Input } from '@/shared/shadcn';
import { MAP_OPTIONS } from '@/entities';
import { useTranslation } from 'react-i18next';
import { usePointOrderDelivery } from './hooks';
import { cn, getYandexLang } from '@/shared/lib';
import { ControlButtons } from './ControlButtons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InputErrorMessage, Typography } from '@/shared/components';
import { YMaps, Map, Clusterer, Placemark } from '@pbe/react-yandex-maps';
import { LocationPointDto, OrderPointAreaDto, OrderPointDto } from '@/app/store/api/restApi';
import { useAppDispatch, useAppSelector, setOrderPointError, selectIsMinimalistic } from '@/app/store';
import {
  OrderPointsInfoCard,
  OrderPointSuggester,
  getCartTotalWeightKg,
  useChangePersonalInfoOrderPoint,
} from '@/features';

export type InputedCityInfoType = {
  city?: string;
  full?: string;
  area?: OrderPointAreaDto;
  coordsCity?: LocationPointDto;
};

export const OrderPointDelivery = ({
  isOrderAnotherPerson,
  onClose,
  noTopMargin,
}: {
  isOrderAnotherPerson: boolean;
  onClose: VoidFunction;
  noTopMargin?: boolean;
}) => {
  const isRTL = useRTL();
  const dispatch = useAppDispatch();
  const { i18n, t } = useTranslation();
  const { t: tAddress } = useTranslation('address');
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const { orderPointDelivery, onSetDeliveryOrderPointAddress, onSetDeliveryOrderPointCity, onSetUserOrderPoint } =
    useChangePersonalInfoOrderPoint(isOrderAnotherPerson);

  const { city: opCity, fullVariant: opFullCity } = orderPointDelivery.orderPointCity ?? {};

  const [inputedCityInfo, setInputedCityInfo] = useState<InputedCityInfoType>({
    city: opCity,
    full: opFullCity,
    coordsCity: orderPointDelivery.orderPointCityCoords,
    area: orderPointDelivery.orderPointArea,
  });

  const [orderPointList, setOrderPointList] = useState<OrderPointDto[]>([]);
  const [selectedPointsArray, setSelectedPointsArray] = useState<OrderPointDto[]>([]);
  const [selectedOrderPoint, setSelectedOrderPoint] = useState<OrderPointDto | null>(null);

  const [mapError, setMapError] = useState('');
  const [cityError, setCityError] = useState('');

  const [showSuggester, setShowSuggester] = useState(false);
  const [selectCity, setSelectCity] = useState(opFullCity ?? '');
  const [orderPointDeliveryInitial] = useState(orderPointDelivery);

  const initCoords = orderPointDelivery.userOrderPoint
    ? [orderPointDelivery.userOrderPoint.location.latitude, orderPointDelivery.userOrderPoint.location.longitude]
    : undefined;

  const [pointCoords, setPointCoords] = useState<number[] | undefined>(
    initCoords ??
      (orderPointDelivery.orderPointCityCoords
        ? [orderPointDelivery.orderPointCityCoords.lat, orderPointDelivery.orderPointCityCoords.lng]
        : undefined),
  );

  const mapState = useMemo(
    () => ({ center: pointCoords || [58.010455, 56.229443], zoom: 15, controls: [] }),
    [pointCoords],
  );

  const { products, unavailableProductIds } = useAppSelector((state) => state.basket);

  const totalCartWeightKg = useMemo(
    () => getCartTotalWeightKg(products, unavailableProductIds),
    [products, unavailableProductIds],
  );

  // CDEK find endpoint takes only an area, so weight filtering happens client-side:
  // a point can carry the cart only if its max weight is unset or >= the cart weight.
  const isPointWithinWeight = useCallback(
    (point: OrderPointDto) => point.weightMaxKg == null || point.weightMaxKg >= totalCartWeightKg,
    [totalCartWeightKg],
  );

  const filteredOrderPointList = useMemo(
    () => orderPointList.filter(isPointWithinWeight),
    [orderPointList, isPointWithinWeight],
  );

  const noPointsForWeight = orderPointList.length > 0 && filteredOrderPointList.length === 0;

  const [pointUnavailableMessage, setPointUnavailableMessage] = useState('');

  const { onLoadMap, mapRef, fetchOrderPoints, isLoading } = usePointOrderDelivery({
    setMapError,
    setCityError,
    setPointCoords,
    inputedCityInfo,
    setOrderPointList,
    selectedOrderPoint,
    setSelectedOrderPoint,
    setSelectedPointsArray,
  });

  useEffect(() => {
    const { city, full } = inputedCityInfo;

    if (city && full && full !== selectCity) {
      setCityError('');
      setSelectCity(full);
      fetchOrderPoints();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderPointDelivery.orderPointCity, inputedCityInfo]);

  useEffect(() => {
    fetchOrderPoints(orderPointDelivery.userOrderPoint?.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the cart changes so the selected point can no longer carry it, drop the
  // selection and tell the customer the point is unavailable for this order.
  useEffect(() => {
    if (selectedOrderPoint && !filteredOrderPointList.some((p) => p.id === selectedOrderPoint.id)) {
      setSelectedOrderPoint(null);
      setSelectedPointsArray([]);
      setPointUnavailableMessage(tAddress('map.orderPointUnavailableForOrder'));
    } else if (selectedOrderPoint) {
      setPointUnavailableMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOrderPointList, selectedOrderPoint]);

  const onSaveOrderPoint = useCallback(
    (orderPointAddress?: string, orderPoint?: OrderPointDto) => {
      const { city, full, coordsCity, area } = inputedCityInfo;

      onSetDeliveryOrderPointCity({
        city: city ?? '',
        fullCity: full ?? '',
        coordsCity: coordsCity,
        coordsArea: area,
      });

      dispatch(setOrderPointError(undefined));
      onSetDeliveryOrderPointAddress(orderPointAddress ?? undefined);
      onSetUserOrderPoint(orderPoint ?? undefined);

      onClose();
    },
    [
      inputedCityInfo,
      onSetDeliveryOrderPointCity,
      dispatch,
      onSetDeliveryOrderPointAddress,
      onSetUserOrderPoint,
      onClose,
    ],
  );

  const onCancelModal = useCallback(() => {
    const { orderPointCity, orderPointAddress, orderPointCityCoords, userOrderPoint, orderPointArea } =
      orderPointDeliveryInitial;

    onSetDeliveryOrderPointCity({
      city: orderPointCity?.city ?? '',
      fullCity: orderPointCity?.fullVariant ?? '',
      coordsCity: orderPointCityCoords,
      coordsArea: orderPointArea,
    });
    onSetDeliveryOrderPointAddress(orderPointAddress);
    onSetUserOrderPoint(userOrderPoint);
    onClose();
  }, [
    onClose,
    onSetUserOrderPoint,
    orderPointDeliveryInitial,
    onSetDeliveryOrderPointCity,
    onSetDeliveryOrderPointAddress,
  ]);

  const onClusterClick = useCallback(
    (e: ymaps.IEvent) => {
      const cluster = e.get('target');
      const points = cluster.properties.get('geoObjects');

      if (points && mapRef.current?.getZoom() >= 20) {
        const pointsInfo: OrderPointDto[] = [];
        const count = points.length;

        for (let i = 0; i < count; i++) {
          pointsInfo.push(points[i].properties.get('point'));
        }

        setSelectedPointsArray(pointsInfo);
        setSelectedOrderPoint(pointsInfo[0]);
      }
    },
    [mapRef],
  );

  return (
    <div className='flex flex-col justify-between h-full'>
      <div className='relative flex flex-col gap-3 w-full h-full'>
        {!showSuggester && (
          <div className={cn('relative mt-4', noTopMargin && 'mt-0')}>
            <Typography
              variant='subtitle3'
              className={cn(
                'absolute top-[-6px] text-muted-foreground bg-background px-[2px] z-[2]',
                isRTL ? 'right-4' : 'left-4',
              )}
            >
              {t('details.city', { ns: 'orders' })}
            </Typography>

            <Input
              placeholder={tAddress('map.enterCity')}
              value={selectCity}
              onChange={(e) => setSelectCity(e.target.value)}
              onMouseDown={(e) => {
                e.stopPropagation();
                setShowSuggester(true);
              }}
            />
          </div>
        )}
        {showSuggester && (
          <OrderPointSuggester
            className={cn('z-10 mt-4', noTopMargin && 'mt-0')}
            inputedCityInfo={inputedCityInfo}
            setInputedCityInfo={setInputedCityInfo}
            onSuccess={() => setShowSuggester(false)}
            inputClassName={mapError && 'border-destructive'}
          />
        )}
        {cityError && <InputErrorMessage text={cityError} className='mt-0' />}
        {noPointsForWeight && <InputErrorMessage text={tAddress('map.noOrderPointsForWeight')} className='mt-0' />}
        {pointUnavailableMessage && <InputErrorMessage text={pointUnavailableMessage} className='mt-0' />}

        <YMaps
          query={{
            lang: getYandexLang(i18n.language),
            load: 'package.full',
            apikey: import.meta.env.VITE_YANDEX_GEOCODER_API_KEY,
          }}
        >
          <Map
            modules={['Placemark', 'geocode', 'geoObject.addon.balloon', 'control.SearchControl', 'Clusterer']}
            instanceRef={mapRef}
            onLoad={(ympasInstance) => onLoadMap(ympasInstance)}
            state={mapState}
            options={MAP_OPTIONS}
            controls={['zoomControl', 'fullscreenControl']}
            className={cn('w-full h-full overflow-hidden relative', !isMinimalistic && 'rounded-md')}
          >
            <Clusterer
              options={{
                preset: 'islands#darkGreenClusterIcons',
                groupByFirstSignificantAddress: false,
                zoomMargin: 60,
                hasBalloon: false,
                clusterOpenBalloonOnClick: false,
              }}
              onClick={(e: ymaps.IEvent) => onClusterClick(e)}
            >
              {filteredOrderPointList.map((point) => {
                return (
                  <Placemark
                    key={'map' + point.id}
                    geometry={[point.location.latitude, point.location.longitude]}
                    options={{
                      preset: selectedOrderPoint?.id === point.id ? 'islands#redDotIcon' : 'islands#darkGreenDotIcon',
                    }}
                    properties={{ point: point }}
                    onClick={() => {
                      setSelectedPointsArray([point]);
                      setSelectedOrderPoint(point);
                    }}
                  />
                );
              })}
            </Clusterer>

            {(!selectCity || selectCity === '') && (
              <div className='absolute inset-0 pointer-events-auto z-[2] flex items-center justify-center'>
                <div
                  className={cn(
                    'absolute inset-0 backdrop-blur-sm',
                    isMinimalistic ? 'bg-foreground opacity-35' : 'opacity-70  bg-stone-200',
                  )}
                />
                {isMinimalistic ? (
                  <div className='z-[2] flex flex-col items-center'>
                    <div className='rounded-[8px] bg-background pb-[14px] pt-[10px] px-3 w-[198px]'>
                      <Typography variant='subtitle3' className='text-center'>
                        {tAddress('map.inputCity')}
                      </Typography>
                    </div>
                    <div className='w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-background' />
                    <div className='mt-[5px] flex h-[11px] w-[11px] items-center justify-center rounded-full bg-background'>
                      <div className='h-[5px] w-[5px] rounded-full bg-primary' />
                    </div>
                  </div>
                ) : (
                  <div className='rounded-lg bg-background p-2 w-[189px] z-[2]'>
                    <Typography variant='subtitle3' className='text-center'>
                      {tAddress('map.inputCity')}
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </Map>
        </YMaps>
        {mapError ? <div className='text-sm mt-1 text-red-500'>{mapError}</div> : null}

        {selectedPointsArray.length > 0 && selectedOrderPoint && (
          <OrderPointsInfoCard
            isMinimalistic={isMinimalistic}
            selectedOrderPoint={selectedOrderPoint}
            selectedPointsArray={selectedPointsArray}
            setSelectedOrderPoint={setSelectedOrderPoint}
          />
        )}
      </div>

      <ControlButtons
        loading={isLoading}
        onCancelModal={onCancelModal}
        onSaveModal={() => {
          if (!selectedOrderPoint) return onSaveOrderPoint();
          const { city, short } = selectedOrderPoint.location.address;
          onSaveOrderPoint(city + ', ' + short, selectedOrderPoint);
        }}
      />
    </div>
  );
};
